import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { MainLayout } from '../../components/layout/MainLayout';
import { packagingService, PackagingOrder } from '../../api/services/packagingService';
import apiClient from '../../api/client';
import { useAuthStore } from '../../store/authStore';

interface Trimmer {
  SewerId: number;
  Name: string;
}

const Packaging: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const [orders, setOrders] = useState<PackagingOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showScanModal, setShowScanModal] = useState<boolean>(false);
  const [barcodeInput, setBarcodeInput] = useState<string>('');
  const [scanning, setScanning] = useState<boolean>(false);
  const [scanError, setScanError] = useState<string>('');
  const [trimmers, setTrimmers] = useState<Trimmer[]>([]);
  const [selectedTrimmer, setSelectedTrimmer] = useState<number | null>(null);
  const [loadingTrimmers, setLoadingTrimmers] = useState<boolean>(false);
  const [modalStep, setModalStep] = useState<'scan' | 'instructions'>('scan');
  const [printedPartNumber, setPrintedPartNumber] = useState<string>('');
  const [printingGuide, setPrintingGuide] = useState<boolean>(false);
  const [guideMessage, setGuideMessage] = useState<string>('');

  useEffect(() => {
    loadPackagingItems();
  }, []);

  const loadPackagingItems = async () => {
    try {
      setLoading(true);
      const data = await packagingService.getPackagingItems();
      setOrders(data);
    } catch (error) {
      console.error('Error loading packaging items:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrimmers = async () => {
    try {
      setLoadingTrimmers(true);
      const response = await apiClient.get('/labels/trimmers');
      const data = response.data?.data || response.data;
      setTrimmers(data);

      // Auto-select first trimmer if available
      if (data.length > 0) {
        setSelectedTrimmer(data[0].SewerId);
      }
    } catch (error) {
      console.error('Error loading trimmers:', error);
      setScanError('Failed to load trimmers list');
    } finally {
      setLoadingTrimmers(false);
    }
  };

  const handlePrintLabelClick = () => {
    setShowScanModal(true);
    setModalStep('scan');
    setScanError('');
    setBarcodeInput('');
    setSelectedTrimmer(null);
    setPrintedPartNumber('');
    setGuideMessage('');
    loadTrimmers();
  };

  const handleCloseScanModal = () => {
    setShowScanModal(false);
    setModalStep('scan');
    setBarcodeInput('');
    setScanError('');
    setPrintedPartNumber('');
    setGuideMessage('');
  };

  const handlePrintInstructions = async () => {
    setPrintingGuide(true);
    setGuideMessage('');
    try {
      const lookupResponse = await apiClient.get(`/packaging/installation-guide/${encodeURIComponent(printedPartNumber)}`);
      const guide = lookupResponse.data?.data ?? lookupResponse.data;

      if (guide.type === 'none') {
        const msg = guide.seatTypeName
          ? `No installation instructions found for seat type "${guide.seatTypeName}".`
          : 'No installation instructions found for this part.';
        setGuideMessage(msg);
        return;
      }

      if (guide.type === 'html') {
        const fileResponse = await apiClient.get(`/packaging/guide-file/${encodeURIComponent(guide.fileName)}`, {
          responseType: 'text',
        });
        const win = window.open('', '_blank');
        if (win) {
          win.document.open();
          win.document.write(fileResponse.data as string);
          win.document.close();
        }
      } else {
        // PDF — fetch as arraybuffer, open via blob URL
        const fileResponse = await apiClient.get(`/packaging/guide-file/${encodeURIComponent(guide.fileName)}`, {
          responseType: 'arraybuffer',
        });
        const blob = new Blob([fileResponse.data as ArrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch {
      setGuideMessage('Failed to load installation instructions.');
    } finally {
      setPrintingGuide(false);
    }
  };

  const handleDoneFromInstructions = () => {
    handleCloseScanModal();
    loadPackagingItems();
  };

  const findItemByBarcode = (barcode: string) => {
    // Barcode format: OrderNumber-Ordinal-ItemCount
    console.log('Finding item for barcode:', barcode);
    const parts = barcode.split('-');
    console.log('Barcode parts:', parts);

    if (parts.length !== 3) {
      console.log('Invalid barcode format - expected 3 parts, got:', parts.length);
      return null;
    }

    const orderNumber = parts[0];
    const ordinal = parseInt(parts[1]);

    console.log('Looking for order:', orderNumber, 'ordinal:', ordinal);
    console.log('Available orders:', orders.length);

    for (const order of orders) {
      console.log('Checking order:', order.OrderNumber, 'vs', orderNumber);
      if (order.OrderNumber === orderNumber) {
        console.log('Found matching order! Items:', order.Items.length);
        for (const item of order.Items) {
          console.log('Checking item ordinal:', item.Ordinal, 'vs', ordinal);
          if (item.Ordinal === ordinal) {
            console.log('Found matching item!', item);
            return item;
          }
        }
      }
    }

    console.log('No matching item found');
    return null;
  };

  const handleScanSubmit = async () => {
    if (!barcodeInput.trim()) {
      setScanError('Please scan or enter a barcode');
      return;
    }

    if (!selectedTrimmer) {
      setScanError('Please select a trimmer');
      return;
    }

    setScanning(true);
    setScanError('');

    try {
      // Find the item in current orders
      const item = findItemByBarcode(barcodeInput.trim());

      if (!item) {
        console.log('Available orders:', orders.map(o => o.OrderNumber));
        setScanError(`Item not found in packaging list. Barcode: ${barcodeInput.trim()}`);
        setScanning(false);
        return;
      }

      console.log('Found item:', item);

      // Process scan - add to SewingAssignment and ActionLog
      const username = user?.UserName || 'system';
      await apiClient.post(`/labels/${item.Id}/scan`, {
        trimmerId: selectedTrimmer,
        username: username
      });

      // Get label data from API
      const response = await apiClient.get(`/labels/${item.Id}`);
      console.log('API response:', response);
      console.log('Response.data:', response.data);

      // Check if response.data has the success property (standard API response)
      const apiResponse = response.data as any;

      if (apiResponse.success === false) {
        setScanError(apiResponse.error || apiResponse.message || 'Failed to get label data');
        setScanning(false);
        return;
      }

      // Get the label data - could be response.data.data or response.data depending on interceptor
      const labelData = apiResponse.data || apiResponse;
      console.log('Label data to store:', labelData);

      // Store label data in localStorage
      localStorage.setItem('labelData', JSON.stringify(labelData));

      // Open print label page in new window
      window.open('/production/print-label', '_blank');

      // Transition to installation instructions step
      setPrintedPartNumber(item.PartNumber);
      setModalStep('instructions');
    } catch (error: any) {
      console.error('Error processing scan:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error processing barcode';
      setScanError(errorMessage);
    } finally {
      setScanning(false);
    }
  };

  const handleBarcodeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScanSubmit();
    }
  };


  const getStatusDisplay = (item: any) => {
    const { ActionId, Summary, CreateDate } = item.State;

    if (ActionId === 8) {
      return (
        <div>
          <span className="inline-block px-2 py-1 text-xs font-semibold text-white bg-green-600 rounded">
            Label printed
          </span>
          <br />
          <small className="text-gray-500 dark:text-gray-400">
            on {format(new Date(CreateDate), 'M/d/yy h:mm a')}
          </small>
        </div>
      );
    }

    if (ActionId === 9) {
      return (
        <div>
          <div className="text-gray-900 dark:text-gray-100">
            Received
            <br />
            <small className="text-gray-500 dark:text-gray-400">
              on {format(new Date(CreateDate), 'M/d/yy h:mm a')}
            </small>
          </div>
          <span className="inline-block px-2 py-1 text-xs font-semibold text-white bg-green-600 rounded mt-1">
            Ready to ship
          </span>
        </div>
      );
    }

    return <span className="text-gray-900 dark:text-gray-100">{Summary}</span>;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-auto">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading packaging items...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Trimming/Packing Station
              </h2>
            </div>

            <div className="p-6">
              {/* Actions and Count */}
              <div className="flex justify-between items-center mb-4">
                <p className="text-gray-900 dark:text-gray-100">
                  {orders.length} Items in Trimming/Packaging
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrintLabelClick}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Print Label
                  </button>
                  <button
                    onClick={loadPackagingItems}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {/* Orders List */}
              {orders.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No items in packaging
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order, index) => (
                    <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                      {/* Order Header */}
                      <div className="bg-gray-100 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
                          <div className="col-span-1">
                            <p className="text-gray-900 dark:text-gray-100">
                              <strong className="block">{order.CustomerId}</strong>
                              {order.BillToName}
                            </p>
                          </div>
                          <div className="col-span-1 text-center">
                            <p className="text-gray-900 dark:text-gray-100">
                              <strong>Sales Order: <span className="text-blue-600 dark:text-blue-400">{order.OrderNumber}</span></strong>
                              <br />
                              <strong>{order.ItemCount}</strong> Items
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-gray-900 dark:text-gray-100">
                              Order Date: {format(new Date(order.OrderDate), 'M/d/yy')}
                              <br />
                              <strong>{order.Age} days old</strong>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div className="bg-white dark:bg-gray-800">
                        {order.Items.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            className={`grid grid-cols-4 gap-4 p-4 ${
                              itemIndex < order.Items.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''
                            }`}
                          >
                            <div className="col-span-1">
                              <strong className="text-gray-900 dark:text-gray-100">
                                {order.OrderNumber}-{item.Ordinal}-{order.ItemCount}
                              </strong>
                            </div>
                            <div className="col-span-1 text-gray-900 dark:text-gray-100">
                              {item.PartNumber}
                            </div>
                            <div className="col-span-1 text-gray-900 dark:text-gray-100">
                              {item.Type}
                            </div>
                            <div className="col-span-1">
                              {getStatusDisplay(item)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Barcode Scanning Modal */}
      {showScanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">

            {modalStep === 'scan' && (
              <>
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Scan Item Barcode
                  </h3>
                </div>

                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    Select trimmer and scan item barcode:
                  </p>

                  {/* Trimmer Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Trimmer *
                    </label>
                    {loadingTrimmers ? (
                      <div className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded text-gray-500 dark:text-gray-400">
                        Loading trimmers...
                      </div>
                    ) : (
                      <select
                        value={selectedTrimmer || ''}
                        onChange={(e) => setSelectedTrimmer(parseInt(e.target.value))}
                        disabled={scanning || trimmers.length === 0}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Select a trimmer...</option>
                        {trimmers.map((trimmer) => (
                          <option key={trimmer.SewerId} value={trimmer.SewerId}>
                            {trimmer.Name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Barcode Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Item Barcode *
                    </label>
                    <input
                      type="text"
                      value={barcodeInput}
                      onChange={(e) => setBarcodeInput(e.target.value)}
                      onKeyPress={handleBarcodeKeyPress}
                      placeholder="Scan barcode (###-##-##)"
                      disabled={scanning}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Format: OrderNumber-Ordinal-ItemCount (e.g., 1166699-1-3)
                    </p>
                  </div>

                  {scanError && (
                    <div className="mt-3 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 rounded">
                      <p className="text-red-800 dark:text-red-200 text-sm">{scanError}</p>
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 rounded-b-lg flex justify-end gap-2">
                  <button
                    onClick={handleCloseScanModal}
                    disabled={scanning}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleScanSubmit}
                    disabled={scanning || !barcodeInput.trim() || !selectedTrimmer}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {scanning ? 'Processing...' : 'Print Label'}
                  </button>
                </div>
              </>
            )}

            {modalStep === 'instructions' && (
              <>
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Installation Instructions
                  </h3>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 mb-5 text-green-600 dark:text-green-400">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">Label printed for <strong>{printedPartNumber}</strong></span>
                  </div>

                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    Would you like to print installation instructions?
                  </p>

                  {guideMessage && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900 border border-yellow-300 dark:border-yellow-700 rounded text-yellow-800 dark:text-yellow-200 text-sm mb-4">
                      {guideMessage}
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 rounded-b-lg flex justify-end gap-2">
                  <button
                    onClick={handleDoneFromInstructions}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Done
                  </button>
                  <button
                    onClick={handlePrintInstructions}
                    disabled={printingGuide}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {printingGuide ? 'Loading...' : 'Print Instructions'}
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Packaging;
